import type { LaneViewId, MatchViewModel } from "../presentation/game-view-model";
import type { FoundationFixtureMode } from "../fixtures/foundationFixtures";
import { formatResources } from "../presentation/resource-format";
import type { GameEvent } from "@fate-kingdom/engine";

interface GameHudProps {
  game: MatchViewModel;
  fixtureMode: FoundationFixtureMode;
  selectedLaneId: LaneViewId | undefined;
  selectedCardName: string | undefined;
  canAttack: boolean;
  canAdvancePhase: boolean;
  supplyBlocked: boolean;
  commandPanelOpen: boolean;
  lastEvents?: readonly GameEvent[];
  validationMessage: string | undefined;
  onAttack: () => void;
  onAdvancePhase: () => void;
  onReset: () => void;
  onFixtureChange: (mode: FoundationFixtureMode) => void;
}

const PHASE_LABELS: Record<string, string> = {
  ready: "Ready",
  draw: "Draw",
  land: "Land",
  supply: "Supply",
  command: "Command",
  battle: "Battle",
  cleanup: "Cleanup",
  finished: "Finished",
};

export function GameHud({
  game,
  fixtureMode,
  selectedLaneId,
  selectedCardName,
  canAttack,
  canAdvancePhase,
  supplyBlocked,
  commandPanelOpen,
  lastEvents = [],
  validationMessage,
  onAttack,
  onAdvancePhase,
  onReset,
  onFixtureChange,
}: GameHudProps) {
  const selectedLabel =
    selectedCardName ??
    (selectedLaneId
      ? `${selectedLaneId === "center" ? "Centre" : `${selectedLaneId[0]?.toUpperCase()}${selectedLaneId.slice(1)}`} lane`
      : "No card or lane selected");
  const phaseLabel = PHASE_LABELS[game.phase] ?? game.phase;
  const activeLabel = game.activePlayerId === "player-1" ? "You" : "Opponent";
  const compactDock = supplyBlocked || commandPanelOpen;
  const cleanupHand = lastEvents.find(
    (event): event is Extract<GameEvent, { readonly type: "cleanup-hand-discarded" }> =>
      event.type === "cleanup-hand-discarded",
  );
  const clearedPool = lastEvents.find(
    (event): event is Extract<GameEvent, { readonly type: "kingdom-pool-cleared" }> =>
      event.type === "kingdom-pool-cleared",
  );
  const cleanupCompleted = lastEvents.find(
    (event): event is Extract<GameEvent, { readonly type: "cleanup-completed" }> =>
      event.type === "cleanup-completed",
  );
  const cleanupSummary =
    cleanupHand !== undefined && clearedPool !== undefined && cleanupCompleted !== undefined
      ? {
          discardedCount: cleanupHand.cardInstanceIds.length,
          clearedResources: clearedPool.clearedResources,
          nextPlayer: cleanupCompleted.nextActivePlayerId === "player-1" ? "Player 1" : "Player 2",
          nextTurn: cleanupCompleted.nextTurnNumber,
        }
      : undefined;

  return (
    <>
      <header className="match-header">
        <div className="brand-mark" aria-label="Fate Kingdom">
          <span className="brand-mark__kicker">Deterministic foundation</span>
          <strong>FATE KINGDOM</strong>
        </div>

        <div className="turn-status" aria-live="polite">
          <span className="turn-status__phase">{phaseLabel}</span>
          <span>
            Turn {game.turnNumber} · {activeLabel}
          </span>
          <span className="turn-status__event-limit">Events {game.eventLimit}</span>
        </div>

        <div className="banner-score" aria-label="Victory Banners">
          <span>
            You <strong>{game.banners["player-1"]}</strong>
          </span>
          <span aria-hidden="true">◆</span>
          <span>
            Rival <strong>{game.banners["player-2"]}</strong>
          </span>
        </div>
      </header>

      <aside className="match-meta" aria-label="Match status">
        <span>Event Limit {game.eventLimit}</span>
      </aside>

      <section
        className="action-dock"
        aria-label="Game actions"
        data-supply-blocked={supplyBlocked}
        data-compact={compactDock}
      >
        {!compactDock ? (
          <div className="action-dock__selection">
            <span className="eyebrow">Selected</span>
            <strong>{selectedLabel}</strong>
            {validationMessage ? (
              <span className="validation-message" role="status">
                {validationMessage}
              </span>
            ) : (
              <span className="action-hint">
                {selectedCardName
                  ? "Tap a highlighted lane to confirm the engine action."
                  : game.phase === "command"
                    ? "Select an active lane to manage its Commander and Armies."
                    : "Select a Land card or a combat lane."}
              </span>
            )}
          </div>
        ) : null}

        <div className="action-dock__buttons">
          {!compactDock ? (
            <button
              className="action-button action-button--primary"
              type="button"
              disabled={!canAttack}
              data-testid="attack-selected-lane"
              onClick={onAttack}
            >
              Attack lane
            </button>
          ) : null}
          <label className="fixture-picker">
            <span className="sr-only">Foundation scenario</span>
            <select
              aria-label="Foundation scenario"
              data-testid="fixture-picker"
              value={fixtureMode}
              onChange={(event) => onFixtureChange(event.target.value as FoundationFixtureMode)}
            >
              <option value="setup-land">Setup and Found</option>
              <option value="development">Land development</option>
              <option value="supply">Supply choice</option>
              <option value="command-disband">Command disband</option>
              <option value="commander-deployment">Commander deployment</option>
              <option value="army-deployment">Army deployment</option>
              <option value="cleanup">Cleanup handoff</option>
              <option value="combat">Combat proof</option>
            </select>
          </label>
          {!supplyBlocked ? (
            <button
              className="action-button"
              type="button"
              disabled={!canAdvancePhase}
              data-testid="advance-phase"
              onClick={onAdvancePhase}
            >
              Advance phase
            </button>
          ) : null}
          <button
            className="icon-button"
            type="button"
            aria-label="Reset current foundation fixture"
            onClick={onReset}
          >
            ↺
          </button>
        </div>
      </section>

      {cleanupSummary !== undefined ? (
        <section className="cleanup-summary" data-testid="cleanup-summary" aria-live="polite">
          <strong>Turn complete</strong>
          <span>Discarded: {cleanupSummary.discardedCount} cards</span>
          <span>Unused resources cleared: {formatResources(cleanupSummary.clearedResources)}</span>
          <span>
            Next: {cleanupSummary.nextPlayer} — Ready Phase (Turn {cleanupSummary.nextTurn})
          </span>
        </section>
      ) : null}

      {game.status === "finished" ? (
        <section
          className="victory-banner"
          role="dialog"
          aria-modal="false"
          aria-label="Match result"
        >
          <span className="eyebrow">Two lanes claimed</span>
          <strong>
            {game.winnerId === "player-1" ? "The Kingdom is yours" : "The rival claims the Kingdom"}
          </strong>
          <button type="button" onClick={onReset}>
            Reset foundation scenario
          </button>
        </section>
      ) : null}
    </>
  );
}
